import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Loader2, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STORAGE_KEY = "newsletter_popup_v1";
const DELAY_MS = 5000;

const SKIP_PREFIXES = [
  "/sys-x9k2m-panel-7q4w",
  "/firm-panel",
  "/journal",
  "/dashboard",
  "/auth",
  "/accept-invite",
];

const NewsletterPopup = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const skipRoute = SKIP_PREFIXES.some((p) => pathname.startsWith(p));

  useEffect(() => {
    if (skipRoute || user) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const t = window.setTimeout(() => setOpen(true), DELAY_MS);
    return () => window.clearTimeout(t);
  }, [skipRoute, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: "Invalid email", description: "Please enter a valid address.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("subscribe-newsletter", {
        body: { email: trimmed, source: "popup" },
      });
      if (error || !data?.ok) {
        throw new Error(error?.message || "Subscribe failed");
      }
      setDone(true);
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
      toast({ title: "You're in 🎉", description: "Thanks for subscribing." });
      setTimeout(() => setOpen(false), 1800);
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (skipRoute || user) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-labelledby="newsletter-popup-title"
        >
          <motion.div
            className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-[#00BFFF]/30 bg-[#0E141B] shadow-[0_25px_60px_-15px_rgba(0,191,255,0.35)] ring-1 ring-[#00BFFF]/10"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.96 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: reducedMotion ? 0.15 : 0.28, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 z-20 p-1.5 rounded-full text-gray-300 hover:text-white bg-black/20 hover:bg-black/40 backdrop-blur-sm transition-all"
              aria-label="Close newsletter popup"
            >
              <X className="w-4 h-4" />
            </button>

            {done ? (
              <div className="px-7 py-12 text-center">
                <motion.div
                  initial={reducedMotion ? { opacity: 0 } : { scale: 0.5, opacity: 0 }}
                  animate={reducedMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, ease: "backOut" }}
                  className="relative mx-auto mb-5 w-20 h-20"
                >
                  <div className="absolute inset-0 rounded-full bg-[#00BFFF]/20 blur-xl" />
                  <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#00BFFF] to-[#0099CC] flex items-center justify-center shadow-lg shadow-[#00BFFF]/40">
                    <Check className="w-10 h-10 text-[#0E141B]" strokeWidth={3} />
                  </div>
                </motion.div>
                <h3 className="text-2xl font-bold text-[#F2F2F2] mb-2">Welcome aboard 🚀</h3>
                <p className="text-sm text-gray-400">Your first edge drops Sunday at 9am.</p>
              </div>
            ) : (
              <>
                <div className="relative px-7 pt-8 pb-6 overflow-hidden">
                  <div
                    className="absolute inset-0 opacity-90"
                    style={{
                      background:
                        "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,191,255,0.25) 0%, rgba(28,42,54,0.4) 50%, transparent 100%)",
                    }}
                  />
                  <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(0,191,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,191,255,0.5) 1px, transparent 1px)",
                      backgroundSize: "24px 24px",
                      maskImage:
                        "radial-gradient(ellipse 70% 60% at 50% 0%, black 30%, transparent 80%)",
                      WebkitMaskImage:
                        "radial-gradient(ellipse 70% 60% at 50% 0%, black 30%, transparent 80%)",
                    }}
                  />

                  <div className="relative flex flex-col items-center text-center">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                      <span className="text-[11px] font-medium tracking-wide text-gray-200 uppercase">
                        Trader Newsletter
                      </span>
                    </div>

                    <div className="relative mb-1">
                      {!reducedMotion && (
                        <motion.div
                          className="absolute inset-0 rounded-2xl bg-[#00BFFF]/30 blur-xl"
                          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        />
                      )}
                      <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00BFFF] to-[#0099CC] flex items-center justify-center shadow-lg shadow-[#00BFFF]/30">
                        <Mail className="w-6 h-6 text-[#0E141B]" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-7 pb-7">
                  <h3
                    id="newsletter-popup-title"
                    className="text-2xl sm:text-[26px] font-bold text-[#F2F2F2] text-center leading-tight tracking-tight mb-2"
                  >
                    Get the edge most traders <span className="text-[#00BFFF]">never see.</span>
                  </h3>
                  <p className="text-sm text-gray-400 text-center leading-relaxed mb-5">
                    Join <span className="text-[#F2F2F2] font-semibold">4,200+ traders</span> getting weekly prop-firm deals, payout proofs, and exclusive coupon drops — every Sunday, free.
                  </p>

                  <ul className="space-y-2 mb-6">
                    {[
                      "Exclusive discount codes (up to 30% off)",
                      "New payout proofs every week",
                      "Zero spam — 1 email, every Sunday",
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-sm text-gray-300">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-[#00BFFF]/15 flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-[#00BFFF]" strokeWidth={3} />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none z-10" />
                      <Input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        required
                        placeholder="you@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={submitting}
                        aria-label="Email address"
                        className="h-12 pl-10 bg-[#1C2A36] border-gray-700 text-[#F2F2F2] placeholder:text-gray-500 focus-visible:ring-[#00BFFF] focus-visible:border-[#00BFFF]/50 text-base"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="relative w-full h-12 overflow-hidden bg-gradient-to-r from-[#00BFFF] to-[#0099CC] hover:from-[#00BFFF] hover:to-[#00BFFF] text-[#0E141B] font-bold text-base shadow-lg shadow-[#00BFFF]/25 hover:shadow-[#00BFFF]/40 hover:scale-[1.02] transition-all duration-200 group"
                    >
                      <span className="relative flex items-center justify-center gap-2">
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Subscribing…
                          </>
                        ) : (
                          <>
                            Claim my edge
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                          </>
                        )}
                      </span>
                    </Button>
                  </form>

                  <div className="flex items-center justify-center gap-2.5 mt-5">
                    <div className="flex -space-x-2">
                      <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="" loading="lazy" className="w-6 h-6 rounded-full object-cover border-2 border-[#0E141B]" />
                      <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="" loading="lazy" className="w-6 h-6 rounded-full object-cover border-2 border-[#0E141B]" />
                      <img src="https://randomuser.me/api/portraits/men/76.jpg" alt="" loading="lazy" className="w-6 h-6 rounded-full object-cover border-2 border-[#0E141B]" />
                    </div>
                    <p className="text-xs text-gray-400">
                      <span className="text-[#F2F2F2] font-semibold">4,200+</span> traders subscribed this month
                    </p>
                  </div>

                  <p className="text-[11px] text-gray-500 text-center mt-3">
                    🔒 No spam. Unsubscribe in 1 click.
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPopup;
